import { getApiUrl } from '../config/api';

export interface PointsConfig {
  pointCosts: Record<string, number>;
  pointValue: number;
  planAllocations: Record<string, number>;
  featureMapping: Record<string, string>;
}

export interface FeatureLimits {
  [featureName: string]: string; // "Up to X" or "Unlimited" or "❌"
}

class ConfigService {
  private pointsConfig: PointsConfig | null = null;

  /**
   * Fetch points configuration from backend
   */
  async getPointsConfig(): Promise<PointsConfig> {
    if (this.pointsConfig) {
      return this.pointsConfig;
    }

    try {
      const response = await fetch(getApiUrl('/usage/points/config'));
      if (!response.ok) {
        throw new Error('Failed to fetch points configuration');
      }
      
      this.pointsConfig = await response.json();
      return this.pointsConfig;
    } catch (error) {
      console.error('Failed to fetch points config, using fallback:', error);
      
      // Fallback configuration if API fails
      this.pointsConfig = {
        pointCosts: {
          'CAPTION': 7,
          'THUMBNAIL': 18,
          'SCRIPT': 47,
          'VOICE_SYNTHESIS': 67,
          'MUSIC_GENERATION': 13,
          'TITLE_DESCRIPTION': 27,
          'IMAGE_GENERATION': 27,
          'PROMPT': 20,
          'IMAGE_ANALYSIS': 27,
          'VIDEO_GENERATION': 167,
          'VIDEO_OPTIMIZATION': 7,
          'VIDEO_UPSCALING': 10,
          'SOCIAL_SHARING': 0,
        },
        pointValue: 0.003,
        planAllocations: {
          'creator': 0,
          'creator_lite': 7000,
          'creator_pro': 15000,
          'creator_studio': 30000,
          'creator_unlimited': -1,
        },
        featureMapping: {
          'caption': 'CAPTION',
          'thumbnail': 'THUMBNAIL',
          'script': 'SCRIPT',
          'voice_synthesis': 'VOICE_SYNTHESIS',
          'music_generation': 'MUSIC_GENERATION',
          'title_description': 'TITLE_DESCRIPTION',
          'image_generation': 'IMAGE_GENERATION',
          'prompt': 'PROMPT',
          'image_analysis': 'IMAGE_ANALYSIS',
          'video_generation': 'VIDEO_GENERATION',
          'video_optimization': 'VIDEO_OPTIMIZATION',
          'video_upscaling': 'VIDEO_UPSCALING',
          'social_sharing': 'SOCIAL_SHARING',
        }
      };
      
      return this.pointsConfig;
    }
  }

  /**
   * Calculate "Up to X" usage based on points and feature cost
   */
  calculateMaxUsage(points: number, pointCost: number): string {
    if (points === 0) return "❌"; // No points = no access
    if (points === -1) return "Unlimited"; // Unlimited plan
    if (pointCost === 0) return "✅ Unlimited (Free)"; // Free feature
    return `Up to ${Math.floor(points / pointCost)}`;
  }

  /**
   * Get feature limits for a specific plan
   */
  async getFeatureLimits(planName: string): Promise<FeatureLimits> {
    const config = await this.getPointsConfig();
    const points = config.planAllocations[planName.toLowerCase().replace(/\s+/g, '_')] || 0;
    
    const limits: FeatureLimits = {};
    
    // Feature name mapping (frontend display name -> backend key)
    const featureMapping: Record<string, string> = {
      "Video Caption Generation": "CAPTION",
      "AI Thumbnail Creation": "THUMBNAIL", 
      "Script Generation": "SCRIPT",
      "Audio Generation (Narration & Music)": "VOICE_SYNTHESIS",
      "AI Music Generation (Soundtrack and effects)": "MUSIC_GENERATION",
      "Title & Description Generation": "TITLE_DESCRIPTION",
      "AI Image Generation": "IMAGE_GENERATION",
      "AI Prompt Generation": "PROMPT",
      "Image Analysis & Description": "IMAGE_ANALYSIS",
      "AI Video Generation": "VIDEO_GENERATION",
      "AI Video Upscaling (4x)": "VIDEO_UPSCALING",
      "Social Media Direct Sharing": "SOCIAL_SHARING",
      "Veo 3 AI Video Generation": "VIDEO_GENERATION", // VEO uses same cost as regular video
    };

    // Calculate limits for each feature
    Object.entries(featureMapping).forEach(([displayName, backendKey]) => {
      const pointCost = config.pointCosts[backendKey] || 0;
      limits[displayName] = this.calculateMaxUsage(points, pointCost);
    });

    return limits;
  }

  /**
   * Get point cost for a feature
   */
  async getFeaturePointCost(featureName: string): Promise<number> {
    const config = await this.getPointsConfig();
    const featureMapping: Record<string, string> = {
      "Video Caption Generation": "CAPTION",
      "AI Thumbnail Creation": "THUMBNAIL", 
      "Script Generation": "SCRIPT",
      "Audio Generation (Narration & Music)": "VOICE_SYNTHESIS",
      "AI Music Generation (Soundtrack and effects)": "MUSIC_GENERATION",
      "Title & Description Generation": "TITLE_DESCRIPTION",
      "AI Image Generation": "IMAGE_GENERATION",
      "AI Prompt Generation": "PROMPT",
      "Image Analysis & Description": "IMAGE_ANALYSIS",
      "AI Video Generation": "VIDEO_GENERATION",
      "Social Media Direct Sharing": "SOCIAL_SHARING",
      "Veo 3 AI Video Generation": "VIDEO_GENERATION",
    };

    const backendKey = featureMapping[featureName];
    return config.pointCosts[backendKey] || 0;
  }

  /**
   * Clear cached configuration (for testing or when config updates)
   */
  clearCache(): void {
    this.pointsConfig = null;
  }
}

export const configService = new ConfigService();
